/**
 * Step 0 — Diagram mode.
 *
 * Inline picker: lists this project's architecture diagrams,
 * user picks one, clicks "Use This Diagram" to fire
 * `startAnalysis('diagram-{diagramId}', ...)`. Lifecycle in
 * `useDiagramItemDefinition`.
 */
import React from 'react';
import type { DiagramFlowState } from '../stepBodyProps';
import { Button } from '../../../../components/ui/Button2';
import { Spinner } from '../../../../components/ui/Spinner';
import { TypographyMuted } from '../../../../components/ui/Typography';
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription } from '../../../../components/ui/Empty';
import styles from './DiagramMode.module.css';

// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
    });
  } catch {
    return iso;
  }
};

export default function DiagramMode({
  diagrams,
  loading,
  loadError,
  selectedId,
  onSelect,
  submitting,
  submitError,
  onSubmit,
}: DiagramFlowState) {
  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <h2 className={styles.title}>Architecture Diagram</h2>
        <TypographyMuted className={styles.helper}>
          Pick a diagram from this project. Its components and connections will seed the Item Definition.
        </TypographyMuted>
      </header>

      {loading ? (
        <div className={styles.stateMsg}>
          <Spinner /> Loading diagrams…
        </div>
      ) : loadError ? (
        <div className={styles.errorBanner}>{loadError}</div>
      ) : diagrams.length === 0 ? (
        <Empty className={styles.emptyFill}>
          <EmptyHeader>
            <EmptyTitle>No diagrams in this project yet</EmptyTitle>
            <EmptyDescription>
              Create one in the Architecture tab, then return here to import it.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className={styles.listWrap}>
          <ul className={styles.list}>
            {diagrams.map((d) => (
              <li key={d.id}>
                <label className={`${styles.row} ${selectedId === d.id ? styles.rowSelected : ''}`}>
                  <input
                    type="radio"
                    name="diagram"
                    value={d.id}
                    checked={selectedId === d.id}
                    onChange={() => onSelect(d.id)}
                    disabled={submitting}
                    className={styles.radio}
                  />
                  <span className={styles.rowName}>{d.name}</span>
                  <span className={styles.rowMeta}>Updated {formatDate(d.updatedAt)}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      {submitError && <div className={styles.errorBanner}>{submitError}</div>}

      <div className={styles.actions}>
        <ButtonAny
          variant="default"
          loading={submitting}
          disabled={!selectedId || diagrams.length === 0}
          onClick={onSubmit}
        >
          {submitting ? 'Starting analysis…' : 'Use This Diagram'}
        </ButtonAny>
      </div>
    </section>
  );
}
